import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { api, GATE_KEY } from "../../lib/api";
import { formatEventDate } from "../../lib/format";
import { GateFooter } from "./GateLegal";

const LOGO = `${process.env.PUBLIC_URL || ""}/brand/logo-480.webp`;

const useNoindex = () => useEffect(() => {
  const m = document.createElement("meta");
  m.name = "robots"; m.content = "noindex, nofollow";
  document.head.appendChild(m);
  document.title = "Coming Soon | CIArb Bahamas Branch";
  return () => m.remove();
}, []);

// Branded "Coming Soon" gate: one shared password unlocks the whole site; the token is checked server-side.
export default function GatePage({ onUnlocked }) {
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useNoindex();
  useEffect(() => { api.get("/public/gate/info").then((r) => setInfo(r.data)).catch(() => setInfo({})); }, []);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const { data } = await api.post("/public/gate/unlock", { password });
      localStorage.setItem(GATE_KEY, data.gate_token);
      setPassword("");
      onUnlocked();
    } catch (err) {
      const s = err?.response?.status;
      setError(s === 429 ? "Too many attempts. Please wait a few minutes and try again." : s === 401 || s === 422 ? "Incorrect password. Please try again." : "The site is temporarily unavailable. Please try again in a moment.");
    } finally { setBusy(false); }
  };

  const ev = info?.event;
  return (
    <div className="flex min-h-screen flex-col bg-[#0B2545] text-white" data-testid="gate-page">
      <header className="bg-black px-6 py-4"><img src={LOGO} alt="CIArb Bahamas Branch" className="h-10 w-auto" /></header>
      <main className="flex flex-1 items-center px-6 py-16">
        <div className="mx-auto w-full max-w-xl">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D4AF37]">Coming soon</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl">{ev?.name || "Arbitration Day 2026"}</h1>
          {ev && <p className="mt-4 text-base text-white/85 md:text-lg" data-testid="gate-event-line">
            {formatEventDate(ev)}{ev.date && !ev.date_confirmed ? " (to be confirmed)" : ""}{ev.venue ? ` · ${ev.venue}` : ""}</p>}
          <p className="mt-6 text-sm text-white/75" data-testid="gate-note">Private preview – authorised CIArb and project team only.</p>
          <form onSubmit={submit} className="mt-8 rounded-lg bg-white p-6 text-[#1F2937] shadow-lg" data-testid="gate-form">
            <label htmlFor="gate-password" className="flex items-center gap-2 text-sm font-semibold text-[#0B2545]"><Lock size={16} aria-hidden="true" /> Access password</label>
            <div className="mt-2 flex flex-col gap-3 sm:flex-row">
              <input id="gate-password" type="password" autoComplete="current-password" required maxLength={72} value={password}
                onChange={(e) => setPassword(e.target.value)} aria-invalid={Boolean(error)} aria-describedby={error ? "gate-error" : undefined}
                className="flex-1 rounded border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C8102E]" data-testid="gate-password-input" />
              <button type="submit" disabled={busy} className="rounded bg-[#C8102E] px-6 py-2 font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60" data-testid="gate-submit-btn">
                {busy ? "Checking…" : "Enter"}</button>
            </div>
            {error && <p id="gate-error" role="alert" className="mt-3 text-sm text-[#C8102E]" data-testid="gate-error">{error}</p>}
          </form>
          {info?.contact_email && <p className="mt-6 text-sm text-white/75">Questions? <a className="underline" href={`mailto:${info.contact_email}`} data-testid="gate-contact-email">{info.contact_email}</a></p>}
        </div>
      </main>
      <GateFooter />
    </div>
  );
}
